// ABOUTME: Modal for editing child profile information (name, birth info, questions)
// ABOUTME: Includes form validation and loading states for save operation

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid
} from '@mui/material';
import type { EditChildFormState } from '../types';
import NicknamesChipInput from '../../common/NicknamesChipInput';

interface EditChildProfileModalProps {
  open: boolean;
  formData: EditChildFormState;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  onFormChange: (updates: Partial<EditChildFormState>) => void;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EditChildProfileModal: React.FC<EditChildProfileModalProps> = ({
  open,
  formData,
  saving,
  onClose,
  onSave,
  onFormChange
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  const handleYearChange = (newYear: number) => {
    let newBirthMonth = formData.birthMonth;
    // Auto-clear birth month if switching to current year and month is now in the future
    if (newYear === currentYear && formData.birthMonth) {
      const monthNum = MONTH_NAMES.indexOf(formData.birthMonth) + 1;
      if (monthNum > currentMonth) {
        newBirthMonth = '';
      }
    }
    onFormChange({ birthYear: newYear, birthMonth: newBirthMonth });
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Child Profile</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={formData.childName}
            onChange={(e) => onFormChange({ childName: e.target.value })}
            margin="normal"
          />
          <NicknamesChipInput
            value={formData.aliases}
            onChange={(aliases) => onFormChange({ aliases })}
            childName={formData.childName}
          />

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="editChildBirthYear-label">Birth Year</InputLabel>
                <Select
                  labelId="editChildBirthYear-label"
                  id="editChildBirthYear"
                  value={formData.birthYear || ''}
                  onChange={(e) => handleYearChange(parseInt(String(e.target.value)) || 0)}
                  label="Birth Year"
                >
                  {Array.from({length: 25}, (_, i) => currentYear - i).map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="editChildBirthMonth-label">Birth Month</InputLabel>
                <Select
                  labelId="editChildBirthMonth-label"
                  id="editChildBirthMonth"
                  value={formData.birthMonth}
                  onChange={(e) => onFormChange({ birthMonth: e.target.value })}
                  label="Birth Month"
                >
                  {MONTH_NAMES.map((monthName, index) => {
                    const monthNum = index + 1;
                    const isDisabled = formData.birthYear === currentYear && monthNum > currentMonth;
                    return (
                      <MenuItem key={monthName} value={monthName} disabled={isDisabled}>
                        {monthName}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <FormControl fullWidth margin="normal">
            <InputLabel>Gender (Optional)</InputLabel>
            <Select
              value={formData.gender || ''}
              onChange={(e) => onFormChange({ gender: e.target.value })}
              label="Gender (Optional)"
            >
              <MenuItem value="">Prefer not to say</MenuItem>
              <MenuItem value="Boy">Boy</MenuItem>
              <MenuItem value="Girl">Girl</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Personal Mantra (Optional)"
            value={formData.mantra}
            onChange={(e) => onFormChange({ mantra: e.target.value })}
            placeholder="e.g., Be kind to everyone"
            margin="normal"
            multiline
            maxRows={2}
            inputProps={{ maxLength: 500 }}
          />

          <TextField
            fullWidth
            label="How does this child show love to family? (Optional)"
            value={formData.familyLoveQuestion}
            onChange={(e) => onFormChange({ familyLoveQuestion: e.target.value })}
            placeholder="e.g., By sharing toys, giving hugs"
            margin="normal"
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />

          <TextField
            fullWidth
            label="What does this child need from family? (Optional)"
            value={formData.familyNeedQuestion}
            onChange={(e) => onFormChange({ familyNeedQuestion: e.target.value })}
            placeholder="e.g., Hugs from mom and dad, playing together"
            margin="normal"
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={saving || !formData.childName.trim()}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditChildProfileModal;
